import { css } from '@/styled-system/css';

export const TWO_COLUMNS = '48em';
// Below this text width the mixed-sentiment summary must remain fully visible.
const THEME_CLAMP_WIDTH = '20.375em';

export const panel = css({
  containerType: 'inline-size',
  containerName: 'reputation',
  color: 'text.neutral',
  minWidth: 0,
});
export const stack = css({ display: 'flex', flexDirection: 'column', gap: 'default', minWidth: 0 });
export const overview = css({
  display: 'grid',
  gap: 'default',
  // eslint-disable-next-line constellation/no-dynamic-css-args -- Panda resolves this colocated literal; verified in rendered CSS.
  [`@container reputation (min-width: ${TWO_COLUMNS})`]: { gridTemplateColumns: '1fr 1fr', gap: 'loose' },
});
export const rating = css({ display: 'flex', alignItems: 'flex-start', gap: 'tight' });
export const score = css({ fontSize: 'heading-lg', lineHeight: 'heading-lg', fontWeight: 'bold', flexShrink: 0 });
export const copy = css({ margin: 0, fontSize: 'body', lineHeight: 'body', overflowWrap: 'anywhere' });
export const subtle = css({ margin: 0, color: 'text.subtle', fontSize: 'body-sm', lineHeight: 'body-sm' });
export const stars = css({ display: 'flex', gap: 'micro.default', color: 'icon.accent.yellow.hero' });
export const star = css({ display: 'inline-flex', position: 'relative' });
export const emptyStar = css({ opacity: '20' });
export const starFill = css({
  position: 'absolute',
  inset: 0,
  overflow: 'hidden',
  '& svg': { maxWidth: 'none', flexShrink: 0 },
});
export const chips = css({
  display: 'flex',
  gap: 'tighter',
  overflowX: 'auto',
  scrollbarWidth: 'none',
  _scrollbar: { display: 'none' },
  paddingBlock: 'micro.default',
  // eslint-disable-next-line constellation/no-dynamic-css-args -- Panda resolves this colocated literal; verified in rendered CSS.
  [`@container reputation (min-width: ${TWO_COLUMNS})`]: { flexWrap: 'wrap' },
  '& > button': { flexShrink: 0 },
});
export const themeCard = css({
  backgroundColor: 'bg.softest',
  borderRadius: 'md',
  padding: 'default',
  containerType: 'inline-size',
  containerName: 'reputation-theme',
});
export const themeLayout = css({ display: 'grid', gap: 'tighter' });
export const title = css({
  margin: 0,
  fontSize: 'body',
  lineHeight: 'body',
  fontWeight: 'bold',
  overflowWrap: 'anywhere',
});
export const summary = css({
  margin: 0,
  fontSize: 'body',
  lineHeight: 'body',
  overflowWrap: 'anywhere',
  '&[data-collapsed=true]': { lineClamp: 2 },
});
export const themeSummary = css({
  margin: 0,
  fontSize: 'body',
  lineHeight: 'body',
  overflowWrap: 'anywhere',
  // eslint-disable-next-line constellation/no-dynamic-css-args -- Panda resolves this colocated literal; verified in rendered CSS.
  [`@container reputation-theme (min-width: ${THEME_CLAMP_WIDTH})`]: { '&[data-collapsed=true]': { lineClamp: 4 } },
});
export const themeExpand = css({
  display: 'none',
  // eslint-disable-next-line constellation/no-dynamic-css-args -- Panda resolves this colocated literal; verified in rendered CSS.
  [`@container reputation-theme (min-width: ${THEME_CLAMP_WIDTH})`]: { display: 'block' },
});
export const header = css({ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 'tighter' });
export const category = css({
  display: 'flex',
  gap: 'tight',
  borderWidth: 'default',
  borderStyle: 'solid',
  borderColor: 'border.muted',
  borderRadius: 'md',
  padding: 'tight',
  minWidth: 0,
  height: '100%',
});
export const categoryDetail = css({ display: 'flex', flexDirection: 'column', gap: 'tightest', flex: 1, minWidth: 0 });
export const categoryIcon = css({
  color: 'text.neutral',
  '&[data-category="billing"]': { color: 'icon.accent.blue.hero' },
  '&[data-category="communication"]': { color: 'icon.accent.purple.hero' },
  '&[data-category="maintenance"]': { backgroundColor: 'blue.100', color: 'icon.accent.blue.hero' },
  '&[data-category="staff"]': { backgroundColor: 'purple.100', color: 'icon.accent.purple.hero' },
  '&[data-category="overall"], &[data-category="community"]': {
    backgroundColor: 'teal.100',
    color: 'icon.accent.teal.hero',
  },
  '&[data-category="facilities"], &[data-category="leasing"], &[data-category="service"]': {
    backgroundColor: 'aqua.100',
    color: 'icon.accent.aqua.strong',
  },
  width: '800',
  height: '800',
  flexShrink: 0,
  borderRadius: 'md',
  display: 'grid',
  placeItems: 'center',
  backgroundColor: 'bg.softest',
});
export const percent = css({ fontWeight: 'bold', whiteSpace: 'nowrap', color: 'text.neutral' });
export const meter = css({
  width: '100%',
  height: '100',
  borderRadius: 'pill',
  backgroundColor: 'border.muted',
  overflow: 'hidden',
});
export const meterFill = css({
  height: '100%',
  borderRadius: 'pill',
  backgroundColor: 'bg.accent.teal.hero',
});
export const highlightsGrid = css({
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr)',
  xl: { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },
  gap: 'tighter',
  alignItems: 'start',
  '& > article': { height: 'auto' },
});
export const carousel = css.raw({
  '& [data-c11n-component="Carousel.Header"]': {
    alignItems: 'center',
    gap: 'tighter',
    paddingBlockEnd: 'default',
  },
  // Constellation hardcodes rail padding; this section owns its spacing.
  '& [data-c11n-component="Carousel.ScrollContainer"]': { paddingBlock: 0 },
  '& [data-c11n-component="Carousel.Slide"]': {
    flexBasis: 'calc(100% - {spacing.loose})!',
    [`@container reputation (min-width: ${TWO_COLUMNS})`]: { flexBasis: 'calc((100% - {spacing.looser}) / 2)!' },
  },
});
export const sheet = css.raw({
  lg: {
    // eslint-disable-next-line hotpads/no-magic-numbers -- Figma specifies a 900px sheet bounded by the viewport.
    width: 'min(900px, calc(100vw - {spacing.looser}))',
    // eslint-disable-next-line hotpads/no-magic-numbers -- Figma specifies a 732px sheet bounded by the viewport.
    height: 'min(732px, calc(100dvh - {spacing.looser}))',
    maxWidth: 'none',
  },
});
export const sheetGrid = css({
  display: 'grid',
  gap: 'tighter',
  gridTemplateColumns: 'minmax(0, 1fr)',
  lg: { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },
});

export const pagination = css({
  display: 'flex',
  justifyContent: 'center',
  border: 0,
  padding: 0,
  margin: 0,
  minWidth: 0,
});
export const pageButton = css({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '{spacing.loose}',
  height: '{spacing.loose}',
  padding: 0,
  border: 0,
  background: 'transparent',
  cursor: 'pointer',
  borderRadius: 'sm',
  _focusVisible: {
    outlineWidth: 'default',
    outlineStyle: 'solid',
    outlineColor: 'border.accent.brand.hero',
    outlineOffset: 'micro.default',
  },
});
export const pageDot = css({
  width: '{spacing.tighter}',
  height: '{spacing.tighter}',
  borderRadius: 'pill',
  backgroundColor: 'border.muted',
  transform: 'scale(0.75)',
  '[aria-current="page"] &': { backgroundColor: 'bg.action.hero.default', transform: 'scale(1)' },
});

export const contactFooter = css.raw({
  justifyContent: 'center',
  flexShrink: 0,
  padding: 0,
  paddingInline: 0,
  paddingBlock: 0,
  background: 'transparent',
  lg: {
    position: 'absolute',
    bottom: 'loose',
    left: 0,
    width: '100%',
  },
});
export const sheetBody = css.raw({ lg: { paddingBlockEnd: 'layout.vast.default' } });
